import React, { useRef, useEffect, useMemo } from 'react'
import {
  QuestionCircleOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons'
import { Col, message, PageHeader, Row, Statistic, Tooltip } from 'antd'
import isEmpty from 'lodash/isEmpty'
import { uuid } from 'uuidv4'
import Loading from 'components/Utils/Loading'
import useGetMerchantCommissionRate from 'components/CustomHooks/CommissionRates/useGetMerchantCommissionRate'
import { netCommissionRate } from './utils'
import './styles.css'

interface StatProps {
  label: string
  value: number
  tooltipMessage: string
}

interface CommissionRate {
  label: string
  tooltip: string
  value: number
}

interface CommissionRatesProps {
  merchantCurrentPlanSlug: string
}

const COLUMN_SPAN = 4
const VAT_PERCENTAGE = '1.2'
const MIN_SCROLL_ITEMS = 7

const CommissionRates = ({ merchantCurrentPlanSlug }: CommissionRatesProps) => {
  const commissionRatesContainer = useRef(null)
  const [commission_rates, { loading, error: getMerchantCommissionRateError }] =
    useGetMerchantCommissionRate({
      merchantCurrentPlanSlug
    })
  const parsedCommissionRates = useMemo(() => {
    const withSameManagedDeliveryRates =
      commission_rates['delivery'] === commission_rates['preorder_delivery']
    const withSameSelfServedDeliveryRates =
      commission_rates['same_day_self_delivery'] ===
      commission_rates['preorder_self_delivery']
    const withSameClickAndCollectRates =
      commission_rates['pickup'] === commission_rates['preorder_pickup']

    let deliveryRates = []

    if (withSameManagedDeliveryRates) {
      deliveryRates.push({
        label: 'Managed delivery',
        value: netCommissionRate(commission_rates['delivery'], VAT_PERCENTAGE)
      })
    } else {
      deliveryRates.push(
        {
          label: 'Managed delivery (same day)',
          value: netCommissionRate(commission_rates['delivery'], VAT_PERCENTAGE)
        },
        {
          label: 'Managed delivery (pre-orders)',
          value: netCommissionRate(
            commission_rates['preorder_delivery'],
            VAT_PERCENTAGE
          )
        }
      )
    }

    if (withSameSelfServedDeliveryRates) {
      deliveryRates.push({
        label: 'Delivery with own drivers/couriers',
        value: netCommissionRate(
          commission_rates['same_day_self_delivery'],
          VAT_PERCENTAGE
        )
      })
    } else {
      deliveryRates.push(
        {
          label: 'Delivery with own drivers/couriers (same day)',
          value: netCommissionRate(
            commission_rates['same_day_self_delivery'],
            VAT_PERCENTAGE
          )
        },
        {
          label: 'Delivery with own drivers/couriers (pre-orders)',
          value: netCommissionRate(
            commission_rates['preorder_self_delivery'],
            VAT_PERCENTAGE
          )
        }
      )
    }

    if (withSameClickAndCollectRates) {
      deliveryRates.push({
        label: 'Click & Collect',
        value: netCommissionRate(commission_rates['pickup'], VAT_PERCENTAGE)
      })
    } else {
      deliveryRates.push(
        {
          label: 'Click & Collect (same day)',
          value: netCommissionRate(commission_rates['pickup'], VAT_PERCENTAGE)
        },
        {
          label: 'Click & Collect (pre-orders)',
          value: netCommissionRate(
            commission_rates['preorder_pickup'],
            VAT_PERCENTAGE
          )
        }
      )
    }

    return [
      ...deliveryRates,
      {
        label: 'Nationwide delivery',
        value: netCommissionRate(
          commission_rates['nationwide_delivery'],
          VAT_PERCENTAGE
        )
      },
      {
        label: 'Table Ordering',
        value: netCommissionRate(commission_rates['table'], VAT_PERCENTAGE)
      }
    ]
  }, [commission_rates])
  const isScrollable = parsedCommissionRates.length >= MIN_SCROLL_ITEMS
  const Stat = ({ label, value, tooltipMessage }: StatProps) => (
    <Col span={COLUMN_SPAN} className='_text-center'>
      <Statistic value={value} className='_mb-16' />
      <div className='_cl-text-secondary'>
        {label}
        {!isEmpty(tooltipMessage) && (
          <Tooltip title={tooltipMessage}>
            <QuestionCircleOutlined className='_ml-4' />
          </Tooltip>
        )}
      </div>
    </Col>
  )

  const scrollHandler = (direction: string) => {
    const container = commissionRatesContainer.current
    const scrollAmount = direction === 'right' ? 400 : -400
    container.scrollTo({
      top: 0,
      left: container.scrollLeft + scrollAmount,
      behavior: 'smooth'
    })
  }

  useEffect(() => {
    if (!isEmpty(getMerchantCommissionRateError)) {
      message.error(
        `Unable to fetch commission rates due to: ${getMerchantCommissionRateError.message}`,
        5
      )
    }
  }, [getMerchantCommissionRateError])

  if (loading) return <Loading />

  return (
    <>
      <PageHeader title='Your commission plan' />
      <Row data-testid='loyaltyStats' justify='center' align='middle'>
        <Col span={1}>
          {isScrollable && (
            <Row className='_mb-0' justify='start' align='middle'>
              <Col>
                <LeftOutlined
                  className='_ml-4 navigation-arrow'
                  onClick={() => scrollHandler('left')}
                />
              </Col>
            </Row>
          )}
        </Col>
        <Col span={22} className='container-style'>
          <Row
            justify={
              parsedCommissionRates.length >= MIN_SCROLL_ITEMS
                ? 'start'
                : 'center'
            }
            ref={commissionRatesContainer}
            className='commission-rates-container _pt-24 _pb-32 _mb-0'
          >
            {parsedCommissionRates.map(
              ({ label, value, tooltip }: CommissionRate) => (
                <Stat
                  key={uuid()}
                  label={label}
                  value={`${value}%`}
                  tooltipMessage={tooltip}
                />
              )
            )}
          </Row>
        </Col>
        <Col span={1}>
          {isScrollable && (
            <Row className='_mb-0' justify='end' align='middle'>
              <Col>
                <RightOutlined
                  className='_ml-4 navigation-arrow'
                  onClick={() => scrollHandler('right')}
                />
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      <Row justify='end' className='_text-right'>
        <Col span={12}>* all pricing displayed is exclusive of VAT</Col>
      </Row>
    </>
  )
}

export default CommissionRates
